import React, { useCallback, useEffect, useRef, useState } from "react";
import { cctvService } from "../services/cctvService";
import HlsPlayer from "../components/HlsPlayer";
import { useI18n } from "../providers/I18nProvider";
import "../styles/CCTV.css";

// Ofis CCTV canlı izleme. 3×3 grid (8 kamera) sub-stream ile oynar.
//  - "Tam Ekran" butonu tüm grid'i (9'lu görünüm) tam ekran kaplar.
//  - Bir kameraya tıklayınca o kamera büyür (tek kamera odağı). Tam ekran
//    container içinde odak değiştiği için tam ekrandan ÇIKMADAN 9'luya dönülür.
//  - Odaktayken tekrar tıklamak veya "9'lu görünüm" butonu 9'luya döner.
// Player'lar hep mount kalır (gizlenir/gösterilir), böylece akış kesilmez.
const requestFs = (el) => {
  if (!el) return;
  const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
  if (fn) fn.call(el).catch(() => {});
};

const CCTVPage = () => {
  const { t } = useI18n();
  const [cameras, setCameras] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [focusedId, setFocusedId] = useState(null);
  const gridRef = useRef(null);

  const loadCameras = useCallback(() => {
    setLoading(true);
    setError("");
    cctvService
      .listCameras()
      .then((cams) => setCameras(Array.isArray(cams) ? cams : []))
      .catch((err) => setError(err.response?.data?.msg || t("cctv.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  // Tam ekrandan çıkınca odağı sıfırla (sonraki açılış 9'lu başlasın).
  useEffect(() => {
    const onFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl) setFocusedId(null);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // Odak modunda Esc ile 9'lu görünüme dön (tam ekran dışındaysa tarayıcı Esc'i yönetmez)
  useEffect(() => {
    if (focusedId == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFocusedId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focusedId]);

  const onCellClick = (cam) => {
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (focusedId === cam.subStreamId) {
      // Aynı kameraya tekrar tıklama → 9'lu görünüme dön.
      setFocusedId(null);
    } else {
      setFocusedId(cam.subStreamId);
      // Henüz tam ekran değilse, container'ı tam ekran yap.
      if (!fsEl) requestFs(gridRef.current);
    }
  };

  const focused = focusedId != null;

  return (
    <div className="cctv-page">
      <div className="cctv-header">
        <div>
          <h2>{t("cctv.title")}</h2>
          <p className="subtitle">{t("cctv.subtitle")}</p>
        </div>
        {cameras.length > 0 && (
          <button
            type="button"
            className="cctv-fs-btn"
            onClick={() => {
              setFocusedId(null);
              requestFs(gridRef.current);
            }}
          >
            ⛶ {t("cctv.fullscreen")}
          </button>
        )}
      </div>

      {error && (
        <div className="cctv-error" role="alert">
          {error}{" "}
          <button type="button" className="cctv-retry-btn" onClick={loadCameras}>
            {t("cctv.retry")}
          </button>
        </div>
      )}
      {loading && <div className="cctv-empty">{t("cctv.loading")}</div>}
      {!loading && !error && cameras.length === 0 && (
        <div className="cctv-empty">{t("cctv.empty")}</div>
      )}

      <div className={`cctv-grid ${focused ? "cctv-focused" : ""}`} ref={gridRef}>
        {focused && (
          <button
            type="button"
            className="cctv-back-btn"
            onClick={() => setFocusedId(null)}
          >
            ◳ {t("cctv.backToGrid")}
          </button>
        )}
        {cameras.map((cam) => (
          <div
            key={cam.subStreamId}
            role="button"
            tabIndex={0}
            className={`cctv-cell ${focusedId === cam.subStreamId ? "cctv-cell-focused" : ""}`}
            onClick={() => onCellClick(cam)}
            onKeyDown={(e) => {
              // iç içe etkileşimli öğe (Tekrar dene) kendi tuşunu yönetir
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCellClick(cam);
              }
            }}
            title={focused ? t("cctv.backToGrid") : t("cctv.expandHint")}
          >
            <HlsPlayer src={cctvService.streamUrl(cam.subStreamId)} flip={cam.flip} />
            <div className="cctv-cell-label">
              <span className="cctv-cell-name">{cam.name}</span>
              {cam.group && <span className="cctv-cell-group">{cam.group}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CCTVPage;
